import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, LogOut, FileText, Download, Loader2, Plus, X, Clock, CheckCircle2, Eye, EyeOff, Calendar, MessageSquare, Send, Lock, Paperclip } from "lucide-react";
import ChatWidget from "@/components/landing/ChatWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detail(d) {
  if (!d) return "Etwas ist schiefgelaufen.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || "").join(" ");
  return String(d);
}

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_LABEL = { offen: "Offen", erledigt: "Erledigt", widerrufen: "Widerrufen", angenommen: "Angenommen", in_bearbeitung: "In Bearbeitung", geloest: "Gelöst", vorgeschlagen: "Vorgeschlagen", ausstehend: "Ausstehend", bestaetigt: "Bestätigt", storniert: "Storniert" };
const statusLabel = (s) => STATUS_LABEL[s] || (s ? s[0].toUpperCase() + s.slice(1) : "");

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1a6.2 6.2 0 1 1 0-12.4c1.77 0 2.96.75 3.64 1.4l2.48-2.4C16.46 3.1 14.44 2.2 12 2.2A9.8 9.8 0 1 0 12 21.8c5.66 0 9.4-3.98 9.4-9.58 0-.64-.07-1.13-.16-1.62H12z" />
    </svg>
  );
}

export default function Portal() {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=auth
  const [mode, setMode] = useState("login");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetPw, setResetPw] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [offers, setOffers] = useState([]);
  const [previews, setPreviews] = useState({});
  const [reqForm, setReqForm] = useState({ title: "", ort: "", einsatzort_adresse: "", wunschdatum: "", beschreibung: "" });
  const [reqImages, setReqImages] = useState([]);
  const [apptProposal, setApptProposal] = useState({ title: "", date: "", start: "", end: "", location: "", note: "" });
  const [savingProposal, setSavingProposal] = useState(false);
  const [savingReq, setSavingReq] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [ticketForm, setTicketForm] = useState({ subject: "", message: "", mission_ref: "" });
  const [ticketReplies, setTicketReplies] = useState({});
  const [savingTicket, setSavingTicket] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const chatFileRef = useRef(null);
  const chatEndRef = useRef(null);
  const googleProcessed = useRef(false);

  useEffect(() => {
    document.title = "Kundenportal — DROPS TFR";
    window.scrollTo(0, 0);
    const t = new URLSearchParams(window.location.search).get("token");
    if (t) { setResetToken(t); setMode("reset"); }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      if (googleProcessed.current) return;
      googleProcessed.current = true;
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      window.history.replaceState({}, "", "/portal");
      axios.post(`${API}/auth/google/session`, {}, { withCredentials: true, headers: { "X-Session-ID": sid } })
        .then((res) => { setUser(res.data); toast.success("Willkommen — via Google angemeldet"); })
        .catch((err) => { toast.error(detail(err.response?.data?.detail)); setUser(false); });
      return;
    }
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => (res.data.role === "customer" ? setUser(res.data) : setUser(false)))
      .catch(() => setUser(false));
  }, []);

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loadData = () => {
    axios.get(`${API}/portal/requests`, { withCredentials: true }).then((r) => setRequests(r.data)).catch(() => {});
    axios.get(`${API}/portal/documents`, { withCredentials: true }).then((r) => setDocuments(r.data)).catch(() => {});
    axios.get(`${API}/portal/offers`, { withCredentials: true }).then((r) => setOffers(r.data)).catch(() => {});
    axios.get(`${API}/portal/tickets`, { withCredentials: true }).then((r) => setTickets(r.data)).catch(() => {});
    axios.get(`${API}/portal/appointments`, { withCredentials: true }).then((r) => setAppointments(r.data)).catch(() => {});
  };
  useEffect(() => { if (user && user.role === "customer") loadData(); }, [user]);

  const loadChat = () => axios.get(`${API}/portal/chat`, { withCredentials: true }).then((r) => setChatMsgs(r.data)).catch(() => {});
  useEffect(() => {
    if (!user || user.role !== "customer") return;
    loadChat();
    const iv = setInterval(loadChat, 4000);
    return () => clearInterval(iv);
  }, [user]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMsgs]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatSending) return;
    setChatSending(true);
    try { await axios.post(`${API}/portal/chat`, { text }, { withCredentials: true }); setChatInput(""); await loadChat(); }
    catch { toast.error("Nachricht konnte nicht gesendet werden"); }
    finally { setChatSending(false); }
  };
  const sendChatImage = async (file) => {
    if (!file) return;
    setChatSending(true);
    try { const fd = new FormData(); fd.append("file", file); await axios.post(`${API}/portal/chat/upload`, fd, { withCredentials: true }); await loadChat(); }
    catch { toast.error("Anhang konnte nicht gesendet werden"); }
    finally { setChatSending(false); if (chatFileRef.current) chatFileRef.current.value = ""; }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/login`, login, { withCredentials: true });
      if (res.data.role !== "customer") { toast.error("Dies ist ein Kundenlogin."); return; }
      setUser(res.data);
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/register`, reg, { withCredentials: true });
      setUser(res.data);
      toast.success("Konto erstellt — willkommen!");
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail }, { withCredentials: true });
      toast.success("Falls ein Konto existiert, haben wir dir einen Link geschickt.");
      setMode("login");
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: resetPw }, { withCredentials: true });
      toast.success("Passwort geändert — bitte melde dich an.");
      window.history.replaceState({}, "", "/portal");
      setMode("login"); setResetPw("");
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false); setRequests([]); setDocuments([]);
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setSavingReq(true);
    try {
      const fd = new FormData();
      fd.append("title", reqForm.title);
      fd.append("ort", reqForm.ort);
      fd.append("einsatzort_adresse", reqForm.einsatzort_adresse);
      fd.append("wunschdatum", reqForm.wunschdatum);
      fd.append("beschreibung", reqForm.beschreibung);
      reqImages.forEach((f) => fd.append("images", f));
      await axios.post(`${API}/portal/requests`, fd, { withCredentials: true });
      setReqForm({ title: "", ort: "", einsatzort_adresse: "", wunschdatum: "", beschreibung: "" });
      setReqImages([]);
      toast.success("Einsatz-Anfrage übermittelt");
      loadData();
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
    finally { setSavingReq(false); }
  };

  const submitProposal = async (e) => {
    e.preventDefault();
    if (!apptProposal.title.trim() || !apptProposal.date.trim()) { toast.error("Titel und Wunschdatum erforderlich"); return; }
    setSavingProposal(true);
    try {
      await axios.post(`${API}/portal/appointments`, apptProposal, { withCredentials: true });
      setApptProposal({ title: "", date: "", start: "", end: "", location: "", note: "" });
      toast.success("Wunschtermin vorgeschlagen — das Team meldet sich");
      loadData();
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
    finally { setSavingProposal(false); }
  };

  const withdrawRequest = async (id) => {
    try {
      await axios.patch(`${API}/portal/requests/${id}/withdraw`, {}, { withCredentials: true });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status: "widerrufen" } : r)));
      toast.success("Anfrage widerrufen");
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject.trim() || !ticketForm.message.trim()) { toast.error("Betreff und Nachricht sind erforderlich"); return; }
    setSavingTicket(true);
    try {
      await axios.post(`${API}/portal/tickets`, ticketForm, { withCredentials: true });
      const r = await axios.get(`${API}/portal/tickets`, { withCredentials: true });
      setTickets(r.data);
      setTicketForm({ subject: "", message: "", mission_ref: "" });
      toast.success("Support-Anfrage gesendet");
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
    finally { setSavingTicket(false); }
  };

  const replyMyTicket = async (id) => {
    const text = (ticketReplies[id] || "").trim();
    if (!text) { toast.error("Bitte eine Nachricht eingeben"); return; }
    try {
      await axios.post(`${API}/portal/tickets/${id}/reply`, { message: text }, { withCredentials: true });
      const r = await axios.get(`${API}/portal/tickets`, { withCredentials: true });
      setTickets(r.data);
      setTicketReplies({ ...ticketReplies, [id]: "" });
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
  };

  const downloadDoc = async (id, title) => {
    try {
      const res = await axios.get(`${API}/portal/document/${id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title || "dokument"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  const downloadOffer = async (id, title) => {
    try {
      const res = await axios.get(`${API}/portal/offer/${id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${title || "angebot"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  const togglePreview = async (id) => {
    if (previews[id]) {
      URL.revokeObjectURL(previews[id]);
      setPreviews((p) => { const n = { ...p }; delete n[id]; return n; });
      return;
    }
    try {
      const res = await axios.get(`${API}/portal/offer/${id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviews((p) => ({ ...p, [id]: url }));
    } catch { toast.error("Vorschau fehlgeschlagen"); }
  };

  const offerAction = async (id, action) => {
    try {
      await axios.patch(`${API}/portal/offers/${id}/${action}`, {}, { withCredentials: true });
      const s = action === "accept" ? "angenommen" : "widerrufen";
      setOffers((os) => os.map((o) => (o.id === id ? { ...o, status: s, decided_at: new Date().toISOString(), decided_by_name: user?.name || "" } : o)));
      toast.success(action === "accept" ? "Angebot digital zugesagt" : "Angebot widerrufen");
    } catch (err) { toast.error(detail(err.response?.data?.detail)); }
  };

  const field = "mt-2 w-full border-b border-white/20 bg-transparent py-3 font-tac text-sm text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none";
  const label = "font-tac text-[10px] uppercase tracking-[0.3em] text-white/50";

  return (
    <div className="min-h-screen bg-[#09090B] text-white selection:bg-[#F5B301] selection:text-black">
      <div className="grain" aria-hidden="true" />
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-xl">
        <div className="flex items-center justify-between px-6 py-4 lg:px-16">
          <a href="/" className="group flex items-center gap-3" data-testid="portal-logo">
            <img src="/assets/logo.png" alt="DROPS TFR" className="h-10 w-10 object-contain" />
            <span className="font-display text-lg font-black uppercase tracking-tighter">
              Drops<span className="text-[#F5B301]"> TFR</span> · Portal
            </span>
          </a>
          {user && user.role === "customer" ? (
            <button data-testid="portal-logout" onClick={doLogout} className="flex items-center gap-2 font-tac text-xs tracking-[0.2em] text-white/70 transition-colors hover:text-[#F5B301]">
              <LogOut className="h-4 w-4" /> ABMELDEN
            </button>
          ) : (
            <a href="/" className="flex items-center gap-2 font-tac text-xs tracking-[0.2em] text-white/70 transition-colors hover:text-white">
              <ArrowLeft className="h-4 w-4" /> STARTSEITE
            </a>
          )}
        </div>
      </header>

      <main className="px-6 py-14 lg:px-16" data-testid="portal-page">
        {user === null ? (
          <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#F5B301]" /></div>
        ) : !user ? (
          <div className="mx-auto max-w-md pt-8">
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">KUNDENPORTAL</p>
            <h1 className="mt-5 font-display text-4xl font-black uppercase tracking-tighter">
              {mode === "login" ? "Anmelden" : mode === "register" ? "Registrieren" : mode === "forgot" ? "Passwort vergessen" : "Neues Passwort"}
            </h1>
            {mode === "forgot" ? (
              <form onSubmit={doForgot} className="mt-8 space-y-6" data-testid="portal-forgot-form">
                <label className="block"><span className={label}>E-Mail</span>
                  <input data-testid="portal-forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className={field} /></label>
                <button data-testid="portal-forgot-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} LINK ANFORDERN
                </button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-center font-tac text-xs text-white/50 hover:text-[#F5B301]">Zurück zum Login</button>
              </form>
            ) : mode === "reset" ? (
              <form onSubmit={doReset} className="mt-8 space-y-6" data-testid="portal-reset-form">
                <label className="block"><span className={label}>Neues Passwort (min. 6 Zeichen)</span>
                  <input data-testid="portal-reset-password" type="password" required value={resetPw} onChange={(e) => setResetPw(e.target.value)} className={field} /></label>
                <button data-testid="portal-reset-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} PASSWORT SPEICHERN
                </button>
              </form>
            ) : mode === "login" ? (
              <form onSubmit={doLogin} className="mt-8 space-y-6" data-testid="portal-login-form">
                <label className="block"><span className={label}>E-Mail</span>
                  <input data-testid="portal-login-email" type="email" required value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} className={field} /></label>
                <label className="block"><span className={label}>Passwort</span>
                  <input data-testid="portal-login-password" type="password" required value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} className={field} /></label>
                <button data-testid="portal-login-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} ANMELDEN
                </button>
                <button type="button" data-testid="portal-show-register" onClick={() => setMode("register")} className="w-full text-center font-tac text-xs text-white/50 hover:text-[#F5B301]">
                  Noch kein Konto? Jetzt registrieren
                </button>
                <button type="button" data-testid="portal-show-forgot" onClick={() => setMode("forgot")} className="w-full text-center font-tac text-xs text-white/40 hover:text-[#F5B301]">
                  Passwort vergessen?
                </button>
              </form>
            ) : (
              <form onSubmit={doRegister} className="mt-8 space-y-6" data-testid="portal-register-form">
                <label className="block"><span className={label}>Name</span>
                  <input data-testid="portal-register-name" type="text" required value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} className={field} /></label>
                <label className="block"><span className={label}>E-Mail</span>
                  <input data-testid="portal-register-email" type="email" required value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} className={field} /></label>
                <label className="block"><span className={label}>Passwort (min. 6 Zeichen)</span>
                  <input data-testid="portal-register-password" type="password" required value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className={field} /></label>
                <button data-testid="portal-register-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} KONTO ERSTELLEN
                </button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-center font-tac text-xs text-white/50 hover:text-[#F5B301]">
                  Bereits Kunde? Zum Login
                </button>
              </form>
            )}
            {(mode === "login" || mode === "register") && (
              <div className="mt-8" data-testid="portal-google-block">
                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="font-tac text-[10px] tracking-[0.3em] text-white/30">ODER</span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <button type="button" data-testid="portal-google-login" onClick={startGoogle} className="mt-6 flex w-full items-center justify-center gap-3 border border-white/20 bg-white/[0.03] px-8 py-4 font-tac text-xs tracking-[0.2em] text-white transition-colors hover:border-[#F5B301] hover:text-[#F5B301]">
                  <GoogleIcon /> MIT GOOGLE ANMELDEN
                </button>
                <p className="mt-3 text-center font-tac text-[10px] text-white/30">Ohne Passwort — sicher über dein Google-Konto</p>
              </div>
            )}
          </div>
        ) : (
          <div className="mx-auto max-w-5xl">
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">WILLKOMMEN, {user.name?.toUpperCase()}</p>
            <h1 className="mt-4 font-display text-4xl font-black uppercase tracking-tighter lg:text-5xl">Dein Portal.</h1>

            {user.customer_status && user.customer_status !== "approved" && (
              <div data-testid="portal-status-banner" className={`mt-6 border p-4 font-tac text-xs leading-relaxed ${user.customer_status === "blocked" ? "border-red-500/40 bg-red-500/5 text-red-300" : "border-[#F5B301]/40 bg-[#F5B301]/5 text-[#F5B301]"}`}>
                {user.customer_status === "blocked"
                  ? "Ihr Kundenkonto ist derzeit gesperrt. Bitte kontaktieren Sie DROPS TFR, um es wieder zu aktivieren."
                  : "Ihr Kundenkonto wird gerade geprüft (Freischaltung ausstehend). Sobald ein Admin Sie freischaltet, können Sie eine TFR-Anforderung stellen und Wunschtermine vorschlagen. Dokumente, Angebote, Support und Chat stehen Ihnen bereits zur Verfügung."}
              </div>
            )}

            <div className="mt-12 grid gap-12 lg:grid-cols-2">
              <section>
                <h2 className="flex items-center gap-2 font-tac text-xs tracking-[0.3em] text-[#F5B301]"><Plus className="h-4 w-4" /> TFR ANFORDERN</h2>
                {(user.customer_status || "approved") !== "approved" ? (
                  <div data-testid="portal-tfr-locked" className="mt-6 flex items-start gap-3 border border-white/10 bg-white/[0.02] p-5 font-tac text-xs leading-relaxed text-white/55">
                    <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#F5B301]" />
                    <span>{user.customer_status === "blocked" ? "Ihr Konto ist gesperrt — eine TFR-Anforderung ist nicht möglich." : "Die TFR-Anforderung ist erst nach Freischaltung durch DROPS TFR verfügbar. Ihr Konto wird derzeit geprüft."}</span>
                  </div>
                ) : (
                <form onSubmit={submitRequest} className="mt-6 space-y-5" data-testid="portal-request-form">
                  <label className="block"><span className={label}>Titel *</span>
                    <input data-testid="portal-req-title" required value={reqForm.title} onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })} className={field} placeholder="z. B. Dachinspektion" /></label>
                  <div className="grid grid-cols-2 gap-5">
                    <label className="block"><span className={label}>Ort</span>
                      <input data-testid="portal-req-ort" value={reqForm.ort} onChange={(e) => setReqForm({ ...reqForm, ort: e.target.value })} className={field} /></label>
                    <label className="block"><span className={label}>Wunschdatum</span>
                      <input data-testid="portal-req-datum" value={reqForm.wunschdatum} onChange={(e) => setReqForm({ ...reqForm, wunschdatum: e.target.value })} className={field} placeholder="2026-09-01" /></label>
                  </div>
                  <label className="block"><span className={label}>Abweichende Einsatzort-Adresse (optional)</span>
                    <input data-testid="portal-req-adresse" value={reqForm.einsatzort_adresse} onChange={(e) => setReqForm({ ...reqForm, einsatzort_adresse: e.target.value })} className={field} placeholder="Straße, PLZ, Ort — falls abweichend" /></label>
                  <label className="block"><span className={label}>Beschreibung</span>
                    <textarea data-testid="portal-req-text" rows={3} value={reqForm.beschreibung} onChange={(e) => setReqForm({ ...reqForm, beschreibung: e.target.value })} className={`${field} resize-none`} /></label>
                  <label className="block"><span className={label}>Fotos (optional)</span>
                    <input data-testid="portal-req-images" type="file" accept="image/*" multiple onChange={(e) => setReqImages(Array.from(e.target.files || []))} className="mt-2 w-full font-tac text-xs text-white/60 file:mr-4 file:border file:border-[#F5B301] file:bg-transparent file:px-4 file:py-2 file:font-tac file:text-[10px] file:tracking-[0.2em] file:text-[#F5B301]" />
                    {reqImages.length > 0 && <span className="mt-2 block font-tac text-[10px] text-white/40">{reqImages.length} Foto(s) ausgewählt</span>}
                  </label>
                  <button data-testid="portal-req-submit" disabled={savingReq} className="flex items-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black disabled:opacity-60">
                    {savingReq && <Loader2 className="h-4 w-4 animate-spin" />} ANFRAGE SENDEN
                  </button>
                </form>
                )}

                <h3 className="mt-10 font-tac text-[10px] tracking-[0.3em] text-white/40">MEINE ANFRAGEN</h3>
                <div className="mt-4 space-y-3" data-testid="portal-requests-list">
                  {requests.length === 0 ? (
                    <p className="font-tac text-xs text-white/40">Noch keine Anfragen.</p>
                  ) : requests.map((r) => (
                    <div key={r.id} data-testid={`portal-request-${r.id}`} className="border border-white/10 p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-display text-sm font-black uppercase">{r.title}</span>
                        <span className={`font-tac text-[9px] tracking-[0.2em] ${r.status === "erledigt" ? "text-emerald-400" : r.status === "widerrufen" ? "text-white/40 line-through" : "text-[#F5B301]"}`}>{r.status?.toUpperCase()}</span>
                      </div>
                      <p className="mt-1 font-tac text-xs text-white/50">{r.ort} · {r.wunschdatum}</p>
                      {r.einsatzort_adresse && <p className="mt-1 font-tac text-[11px] text-white/45">Einsatzort: {r.einsatzort_adresse}</p>}
                      {Array.isArray(r.images) && r.images.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2" data-testid={`portal-request-images-${r.id}`}>
                          {r.images.map((_, i) => (
                            <img key={i} src={`${API}/request-image/${r.id}/${i}`} alt="Anfrage-Foto" className="h-16 w-16 border border-white/10 object-cover" />
                          ))}
                        </div>
                      )}
                      {Array.isArray(r.history) && r.history.length > 0 ? (
                        <ol className="mt-3 space-y-1.5 border-l border-white/10 pl-3" data-testid={`portal-request-history-${r.id}`}>
                          {r.history.map((h, i) => (
                            <li key={i} className="flex items-start gap-2 font-tac text-[10px] text-white/50">
                              <Clock className="mt-0.5 h-3 w-3 shrink-0 text-white/30" />
                              <span><span className="text-white/75">{statusLabel(h.status)}</span> · {fmt(h.at)}{h.note ? ` — ${h.note}` : ""}</span>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="mt-2 font-tac text-[10px] text-white/40" data-testid={`portal-request-created-${r.id}`}>Eingereicht am {fmt(r.created_at)}</p>
                      )}
                      {r.status === "offen" && (
                        <button
                          data-testid={`portal-request-withdraw-${r.id}`}
                          onClick={() => withdrawRequest(r.id)}
                          className="mt-3 flex items-center gap-1.5 font-tac text-[10px] tracking-[0.2em] text-white/50 transition-colors hover:text-red-400"
                        >
                          <X className="h-3.5 w-3.5" /> WIDERRUFEN
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="flex items-center gap-2 font-tac text-xs tracking-[0.3em] text-[#F5B301]"><FileText className="h-4 w-4" /> MEINE DOKUMENTE</h2>
                <div className="mt-6 space-y-3" data-testid="portal-documents-list">
                  {documents.length === 0 ? (
                    <p className="font-tac text-xs text-white/40">Noch keine Dokumente. Sobald wir Berichte für dich bereitstellen, erscheinen sie hier.</p>
                  ) : documents.map((d) => (
                    <div key={d.id} data-testid={`portal-doc-${d.id}`} className="flex items-center justify-between border border-white/10 p-4">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-[#F5B301]" />
                        <span className="font-tac text-sm text-white">{d.title}</span>
                      </div>
                      <button data-testid={`portal-doc-download-${d.id}`} onClick={() => downloadDoc(d.id, d.title)} className="flex items-center gap-2 font-tac text-xs tracking-[0.2em] text-white/70 transition-colors hover:text-[#F5B301]">
                        <Download className="h-4 w-4" /> ÖFFNEN
                      </button>
                    </div>
                  ))}
                </div>
              </section>

              <section className="lg:col-span-2" data-testid="portal-livechat">
                <h2 className="flex items-center gap-2 font-tac text-xs tracking-[0.3em] text-[#F5B301]"><MessageSquare className="h-4 w-4" /> LIVE-CHAT MIT DEM TEAM</h2>
                <p className="mt-3 max-w-xl font-tac text-xs leading-relaxed text-white/50">Direkte Nachrichten an unser Team — inklusive Fotos. Wir antworten hier zeitnah.</p>
                <div className="mt-6 border border-white/10 bg-white/[0.02]">
                  <div className="h-80 space-y-3 overflow-y-auto p-4" data-testid="portal-chat-messages">
                    {chatMsgs.length === 0 ? (
                      <p className="font-tac text-xs text-white/40">Noch keine Nachrichten. Schreib uns gern!</p>
                    ) : chatMsgs.map((m) => (
                      <div key={m.id} data-testid={`portal-chat-msg-${m.sender}`} className={`max-w-[80%] border p-2.5 ${m.sender === "customer" ? "ml-auto border-[#F5B301]/30 bg-[#F5B301]/5" : "border-white/10 bg-white/[0.02]"}`}>
                        <p className="font-tac text-[9px] tracking-[0.2em] text-white/40">{m.sender === "customer" ? "DU" : "DROPS TFR"} · {fmt(m.at)}</p>
                        {m.has_image ? (
                          <img src={`${API}/chat-image/${m.id}`} alt="Foto" className="mt-2 max-h-52 w-auto border border-white/10 object-contain" />
                        ) : m.has_file ? (
                          <a href={`${API}/chat-file/${m.id}`} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-2 font-tac text-xs text-[#F5B301] underline"><FileText className="h-4 w-4" /> {m.file_name || "Dokument.pdf"}</a>
                        ) : (
                          <p className="mt-1 font-tac text-xs leading-relaxed text-white/85">{m.text}</p>
                        )}
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                  <div className="flex items-center gap-2 border-t border-white/10 p-3">
                    <input ref={chatFileRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="portal-chat-file" onChange={(e) => sendChatImage(e.target.files[0])} />
                    <button type="button" data-testid="portal-chat-attach" onClick={() => chatFileRef.current?.click()} disabled={chatSending} aria-label="Foto oder PDF senden" className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/20 text-white/70 transition-colors hover:border-[#F5B301] hover:text-[#F5B301] disabled:opacity-60"><Paperclip className="h-4 w-4" /></button>
                    <input data-testid="portal-chat-input" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }} placeholder="Nachricht schreiben…" className="flex-1 border-b border-white/20 bg-transparent py-2 font-tac text-xs text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none" />
                    <button type="button" data-testid="portal-chat-send" onClick={sendChat} disabled={chatSending} aria-label="Senden" className="flex h-9 w-9 shrink-0 items-center justify-center bg-[#F5B301] text-black disabled:opacity-60">{chatSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
                  </div>
                </div>
              </section>

              <section className="lg:col-span-2">
                <h2 className="flex items-center gap-2 font-tac text-xs tracking-[0.3em] text-[#F5B301]"><FileText className="h-4 w-4" /> ANGEBOTE</h2>
                <div className="mt-6 space-y-3" data-testid="portal-offers-list">
                  {offers.length === 0 ? (
                    <p className="font-tac text-xs text-white/40">Aktuell liegen keine Angebote für dich vor.</p>
                  ) : offers.map((o) => (
                    <div key={o.id} data-testid={`portal-offer-${o.id}`} className="border border-white/10 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-[#F5B301]" />
                          <div>
                            <p className="font-tac text-sm text-white">{o.title}</p>
                            <p className={`font-tac text-[9px] tracking-[0.2em] ${o.status === "angenommen" ? "text-emerald-400" : o.status === "abgelaufen" ? "text-red-400" : o.status === "widerrufen" ? "text-white/40 line-through" : "text-[#F5B301]"}`}>{o.status?.toUpperCase()}</p>
                            {o.valid_until && (
                              <p data-testid={`portal-offer-valid-${o.id}`} className={`mt-1 flex items-center gap-1 font-tac text-[9px] ${o.status === "abgelaufen" ? "text-red-400/80" : "text-white/40"}`}>
                                <Calendar className="h-3 w-3" /> Gültig bis {o.valid_until}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button data-testid={`portal-offer-preview-${o.id}`} onClick={() => togglePreview(o.id)} className="flex items-center gap-1.5 border border-white/20 px-3 py-2 font-tac text-[10px] tracking-[0.2em] text-white/80 transition-colors hover:border-[#F5B301] hover:text-[#F5B301]">
                            {previews[o.id] ? <><EyeOff className="h-3.5 w-3.5" /> SCHLIESSEN</> : <><Eye className="h-3.5 w-3.5" /> VORSCHAU</>}
                          </button>
                          <button data-testid={`portal-offer-download-${o.id}`} onClick={() => downloadOffer(o.id, o.title)} className="flex items-center gap-1.5 border border-white/20 px-3 py-2 font-tac text-[10px] tracking-[0.2em] text-white/80 transition-colors hover:border-[#F5B301] hover:text-[#F5B301]">
                            <Download className="h-3.5 w-3.5" /> DOWNLOAD
                          </button>
                          {o.status === "offen" && (
                            <button data-testid={`portal-offer-accept-${o.id}`} onClick={() => offerAction(o.id, "accept")} className="bg-[#F5B301] px-3 py-2 font-tac text-[10px] font-bold tracking-[0.2em] text-black transition-transform hover:scale-[1.03]">
                              DIGITAL ZUSAGEN
                            </button>
                          )}
                          {(o.status === "offen" || o.status === "angenommen") && (
                            <button data-testid={`portal-offer-withdraw-${o.id}`} onClick={() => offerAction(o.id, "withdraw")} className="border border-white/20 px-3 py-2 font-tac text-[10px] tracking-[0.2em] text-white/60 transition-colors hover:border-red-400 hover:text-red-400">
                              WIDERRUFEN
                            </button>
                          )}
                        </div>
                      </div>
                      {previews[o.id] && (
                        <div className="mt-4 overflow-hidden border border-white/10" data-testid={`portal-offer-preview-frame-${o.id}`}>
                          <iframe title={`Angebot ${o.title}`} src={previews[o.id]} className="h-[520px] w-full bg-white" />
                        </div>
                      )}
                      {o.decided_at && (o.status === "angenommen" || o.status === "widerrufen") && (
                        <div data-testid={`portal-offer-signature-${o.id}`} className={`mt-3 flex items-center gap-2 border-t border-white/10 pt-3 font-tac text-[10px] ${o.status === "angenommen" ? "text-emerald-400" : "text-white/40"}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                          <span>{o.status === "angenommen" ? "Digital zugesagt" : "Widerrufen"} am {fmt(o.decided_at)}{o.decided_by_name ? ` von ${o.decided_by_name}` : ""}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <section className="lg:col-span-2" data-testid="portal-termine">
                <h2 className="flex items-center gap-2 font-tac text-xs tracking-[0.3em] text-[#F5B301]"><Calendar className="h-4 w-4" /> MEINE TERMINE</h2>
                <p className="mt-3 max-w-xl font-tac text-xs leading-relaxed text-white/50">Schlage einen Wunschtermin vor — das Team bestätigt ihn oder passt ihn an. Belegte Zeiten anderer Einsätze siehst du gesperrt — ohne Details.</p>
                {(user.customer_status || "approved") === "approved" ? (
                <form onSubmit={submitProposal} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="portal-appt-proposal-form">
                  <label className="block"><span className={label}>Titel *</span>
                    <input data-testid="portal-appt-title" required value={apptProposal.title} onChange={(e) => setApptProposal({ ...apptProposal, title: e.target.value })} className={field} placeholder="z. B. Dachinspektion" /></label>
                  <label className="block"><span className={label}>Wunschdatum *</span>
                    <input data-testid="portal-appt-date" required type="date" value={apptProposal.date} onChange={(e) => setApptProposal({ ...apptProposal, date: e.target.value })} className={`${field} [color-scheme:dark]`} /></label>
                  <label className="block"><span className={label}>Von (optional)</span>
                    <input data-testid="portal-appt-start" type="time" value={apptProposal.start} onChange={(e) => setApptProposal({ ...apptProposal, start: e.target.value })} className={`${field} [color-scheme:dark]`} /></label>
                  <label className="block"><span className={label}>Bis (optional)</span>
                    <input data-testid="portal-appt-end" type="time" value={apptProposal.end} onChange={(e) => setApptProposal({ ...apptProposal, end: e.target.value })} className={`${field} [color-scheme:dark]`} /></label>
                  <label className="block sm:col-span-2"><span className={label}>Einsatzort (optional)</span>
                    <input data-testid="portal-appt-location" value={apptProposal.location} onChange={(e) => setApptProposal({ ...apptProposal, location: e.target.value })} className={field} placeholder="Straße, PLZ, Ort" /></label>
                  <label className="block sm:col-span-2"><span className={label}>Notiz (optional)</span>
                    <textarea data-testid="portal-appt-note" rows={2} value={apptProposal.note} onChange={(e) => setApptProposal({ ...apptProposal, note: e.target.value })} className={`${field} resize-none`} /></label>
                  <div className="sm:col-span-2">
                    <button data-testid="portal-appt-submit" disabled={savingProposal} className="flex items-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black disabled:opacity-60">{savingProposal && <Loader2 className="h-4 w-4 animate-spin" />} WUNSCHTERMIN VORSCHLAGEN</button>
                  </div>
                </form>
                ) : null}
                <h3 className="mt-10 font-tac text-[10px] tracking-[0.3em] text-white/40">GEPLANTE TERMINE</h3>
                <div className="mt-4 border-t border-white/10" data-testid="portal-termine-list">
                  {appointments.length === 0 ? (
                    <p className="py-6 font-tac text-xs text-white/40">Aktuell keine Termine.</p>
                  ) : appointments.map((a) => (
                    <div key={a.id} data-testid={`portal-appt-${a.id}`} className={`grid grid-cols-12 items-center gap-3 border-b border-white/10 py-3 ${a.locked ? "opacity-50" : ""}`}>
                      <span className="col-span-5 font-tac text-xs tracking-[0.15em] text-[#F5B301] sm:col-span-3">{a.date}{a.start ? ` · ${a.start}` : ""}{a.end ? `–${a.end}` : ""}</span>
                      <span className="col-span-6 flex flex-wrap items-center gap-2 font-tac text-sm text-white sm:col-span-4">
                        {a.locked && <Lock className="h-3.5 w-3.5 text-white/40" />}{a.title}
                        {!a.locked && <span data-testid={`portal-termin-status-${a.id}`} className={`px-1.5 py-0.5 font-tac text-[9px] tracking-[0.15em] ${(a.status || "ausstehend") === "bestaetigt" ? "bg-emerald-500/15 text-emerald-400" : (a.status || "ausstehend") === "storniert" ? "bg-red-500/15 text-red-400" : (a.status || "ausstehend") === "vorgeschlagen" ? "bg-sky-500/15 text-sky-400" : "bg-[#F5B301]/15 text-[#F5B301]"}`}>{statusLabel(a.status || "ausstehend").toUpperCase()}</span>}
                      </span>
                      {!a.locked && <span className="col-span-12 font-tac text-[11px] text-white/50 sm:col-span-5">{a.location}{a.note ? ` · ${a.note}` : ""}</span>}
                    </div>
                  ))}
                </div>
              </section>

              <section className="lg:col-span-2" data-testid="portal-support">
                <h2 className="flex items-center gap-2 font-tac text-xs tracking-[0.3em] text-[#F5B301]"><MessageSquare className="h-4 w-4" /> SUPPORT & HILFE</h2>
                <p className="mt-3 max-w-xl font-tac text-xs leading-relaxed text-white/50">Probleme oder Fragen? Erstelle ein Support-Ticket — wir melden uns hier im Portal.</p>
                <form onSubmit={submitTicket} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="portal-ticket-form">
                  <label className="block"><span className={label}>Betreff *</span>
                    <input data-testid="portal-ticket-subject" required value={ticketForm.subject} onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })} className={field} placeholder="z. B. Angebot nicht sichtbar" /></label>
                  <label className="block"><span className={label}>Bezug zu Einsatz (optional)</span>
                    <input data-testid="portal-ticket-ref" value={ticketForm.mission_ref} onChange={(e) => setTicketForm({ ...ticketForm, mission_ref: e.target.value })} className={field} placeholder="z. B. 2026-000050" /></label>
                  <label className="block sm:col-span-2"><span className={label}>Nachricht *</span>
                    <textarea data-testid="portal-ticket-message" required rows={3} value={ticketForm.message} onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })} className={`${field} resize-none`} placeholder="Beschreibe dein Anliegen…" /></label>
                  <div className="sm:col-span-2">
                    <button data-testid="portal-ticket-submit" disabled={savingTicket} className="flex items-center gap-2 bg-[#F5B301] px-8 py-4 font-tac text-xs font-bold tracking-[0.25em] text-black disabled:opacity-60">
                      {savingTicket && <Loader2 className="h-4 w-4 animate-spin" />} TICKET SENDEN
                    </button>
                  </div>
                </form>

                <h3 className="mt-10 font-tac text-[10px] tracking-[0.3em] text-white/40">MEINE TICKETS</h3>
                <div className="mt-4 space-y-4" data-testid="portal-tickets-list">
                  {tickets.length === 0 ? (
                    <p className="font-tac text-xs text-white/40">Noch keine Tickets.</p>
                  ) : tickets.map((t) => (
                    <div key={t.id} data-testid={`portal-ticket-${t.id}`} className="border border-white/10 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-display text-sm font-black uppercase">{t.subject}</span>
                        <span className={`font-tac text-[9px] tracking-[0.2em] ${t.status === "geloest" ? "text-emerald-400" : t.status === "in_bearbeitung" ? "text-[#F5B301]" : "text-white/60"}`}>{statusLabel(t.status)}</span>
                      </div>
                      {t.mission_ref && <p className="mt-1 font-tac text-[10px] text-white/40">Einsatz {t.mission_ref}</p>}
                      <div className="mt-3 space-y-2">
                        {(t.messages || []).map((m) => (
                          <div key={m.id} className={`max-w-[85%] border p-2.5 ${m.sender === "staff" ? "ml-auto border-[#F5B301]/30 bg-[#F5B301]/5" : "border-white/10 bg-white/[0.02]"}`}>
                            <p className="font-tac text-[9px] tracking-[0.2em] text-white/40">{m.sender === "staff" ? "DROPS TFR" : "DU"} · {fmt(m.at)}</p>
                            <p className="mt-1 font-tac text-xs leading-relaxed text-white/80">{m.text}</p>
                          </div>
                        ))}
                      </div>
                      {t.status !== "geloest" && (
                        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                          <input
                            data-testid={`portal-ticket-reply-input-${t.id}`}
                            value={ticketReplies[t.id] || ""}
                            onChange={(e) => setTicketReplies({ ...ticketReplies, [t.id]: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") replyMyTicket(t.id); }}
                            placeholder="Nachricht schreiben…"
                            className="flex-1 border-b border-white/20 bg-transparent py-2 font-tac text-xs text-white placeholder:text-white/25 focus:border-[#F5B301] focus:outline-none"
                          />
                          <button data-testid={`portal-ticket-reply-${t.id}`} onClick={() => replyMyTicket(t.id)} className="flex items-center gap-1.5 border border-white/20 px-4 py-2 font-tac text-[10px] tracking-[0.2em] text-white/80 transition-colors hover:border-[#F5B301] hover:text-[#F5B301]"><Send className="h-3.5 w-3.5" /> SENDEN</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        )}
      </main>
      <ChatWidget />
    </div>
  );
}
